(() => {
  'use strict';
  if (customElements.get('article-product-drawer')) return;

  class ArticleProductDrawer extends HTMLElement {
    connectedCallback() {
      if (this.abort || typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return;
      this.article = this.closest('[data-article-redesign]');
      this.editorial = this.article?.querySelector('[data-article-editorial-body]');
      if (!this.editorial) return;
      this.tab = this.querySelector('.oe-product-drawer__tab');
      this.dialog = this.querySelector('dialog');
      this.heading = this.querySelector('.oe-product-drawer__heading');
      this.scroller = this.querySelector('.oe-product-drawer__scroll');
      this.header = document.querySelector('.section-header');
      this.desktop = matchMedia('(min-width: 990px)');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.abort = new AbortController();
      const on = (target, event, handler, options = {}) => target?.addEventListener(event, handler, { ...options, signal: this.abort.signal });
      this.lastY = window.scrollY;
      this.geometryDirty = true;
      this.tab.hidden = true;
      // Safari can retain :focus-visible when focus is moved by the dialog.
      // Track real input before focus moves, without removing focus itself.
      on(document, 'pointerdown', () => this.setAttribute('data-pointer-focus', ''), { capture: true, passive: true });
      on(document, 'keydown', (event) => {
        if (['Tab', 'Enter', ' ', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) this.removeAttribute('data-pointer-focus');
      }, { capture: true });
      on(this.tab, 'click', () => this.open());
      on(this.querySelector('.oe-product-drawer__close'), 'click', () => this.close());
      on(this.dialog, 'cancel', (event) => { event.preventDefault(); this.close(); });
      on(this.dialog, 'close', () => this.finishClose());
      on(this.dialog, 'keydown', (event) => {
        if (event.key !== 'Tab') return;
        const targets = [...this.dialog.querySelectorAll('button:not([disabled]), a[href]')].filter((element) => this.visible(element));
        const first = targets[0];
        const last = targets[targets.length - 1];
        if (event.shiftKey && (document.activeElement === first || document.activeElement === this.heading)) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault(); first?.focus();
        }
      });
      on(this.dialog, 'pointerdown', (event) => { this.backdropDown = this.isBackdrop(event); });
      on(this.dialog, 'click', (event) => { if (this.backdropDown && this.isBackdrop(event)) this.close(); });
      const grip = this.querySelector('[data-drawer-swipe]');
      on(grip, 'pointerdown', (event) => {
        if (!event.isPrimary || event.button !== 0 || event.target.closest('button, a')) return;
        this.swipe = { id: event.pointerId, x: event.clientX, y: event.clientY };
        grip.setPointerCapture(event.pointerId);
      });
      on(grip, 'pointermove', (event) => {
        if (!this.swipe || this.swipe.id !== event.pointerId) return;
        const dx = event.clientX - this.swipe.x;
        const dy = Math.abs(event.clientY - this.swipe.y);
        if (dy > 15 && dy > Math.abs(dx)) this.swipe = null;
      });
      on(grip, 'pointerup', (event) => {
        const start = this.swipe;
        this.swipe = null;
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x;
        if (dx > 60 && dx > Math.abs(event.clientY - start.y) * 1.5) this.close();
      });
      on(grip, 'pointercancel', () => { this.swipe = null; });
      on(window, 'wheel', (event) => { if (event.deltaY > 0) this.readIntent = true; }, { passive: true });
      on(window, 'touchstart', (event) => { this.touchY = event.touches[0]?.clientY; }, { passive: true });
      on(window, 'touchmove', (event) => {
        if (event.touches[0]?.clientY < this.touchY) this.readIntent = true;
      }, { passive: true });
      on(window, 'keydown', (event) => {
        if (['ArrowDown', 'PageDown', 'End', ' '].includes(event.key) && !event.target.closest('input, textarea, select, [contenteditable]')) this.readIntent = true;
      });
      on(window, 'scroll', () => this.schedule(), { passive: true });
      on(window, 'resize', () => this.invalidate(), { passive: true });
      on(window.visualViewport, 'resize', () => this.invalidate(), { passive: true });
      on(this.desktop, 'change', () => {
        if (this.desktop.matches) this.close(true);
        this.invalidate();
      });
      on(this.motion, 'change', () => {
        if (this.motion.matches) this.cancelNudge();
      });
      on(window, 'pagehide', () => this.close(true));
      on(window, 'pageshow', () => { this.lastY = window.scrollY; this.readIntent = false; this.invalidate(); });
      on(document, 'shopify:section:unload', (event) => {
        if (event.target.contains(this)) this.disconnectedCallback();
      });
      this.resizeObserver = new ResizeObserver(() => this.invalidate());
      this.resizeObserver.observe(this.editorial);
      this.resizeObserver.observe(this.article);
      if (this.header) this.resizeObserver.observe(this.header);
      // Existing theme dialogs and Shopify consent/chat can open independently.
      // The observer only schedules one check per frame, never changes their state.
      this.modalObserver = new MutationObserver((records) => {
        if (records.every((record) => this.contains(record.target))) return;
        if (records.some((record) => record.target === this.header)) this.geometryDirty = true;
        this.schedule();
      });
      this.modalObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'open', 'hidden', 'aria-hidden', 'aria-expanded'] });
      this.schedule();
    }

    invalidate() { this.geometryDirty = true; this.schedule(); }

    schedule() {
      if (this.frame || !this.abort) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
    }

    visible(element) {
      return element && !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
    }

    blocked() {
      const chatRoot = document.querySelector('shopify-chat')?.shadowRoot;
      if (chatRoot && this.chatRoot !== chatRoot) {
        this.chatRoot = chatRoot;
        this.modalObserver.observe(chatRoot, { subtree: true, childList: true, attributes: true, attributeFilter: ['class', 'style', 'aria-expanded', 'aria-hidden'] });
      }
      if (chatRoot?.querySelector('[aria-expanded="true"]')) return true;
      const selectors = 'dialog[open], [role="dialog"][aria-modal="true"], cart-drawer.active, .oe-mobile-drawer[aria-hidden="false"], #shopify-pc__banner, #shopify-pc__prefs';
      return [...document.querySelectorAll(selectors)].some((element) => !this.contains(element) && this.visible(element));
    }

    update() {
      const y = window.scrollY;
      const down = y > this.lastY + 0.5;
      this.lastY = y;
      if (this.desktop.matches) { this.tab.hidden = true; return; }
      const blocked = this.blocked();
      if (blocked && this.dialog.open) this.close(true, false);
      if (this.geometryDirty && !this.dialog.open) {
        const box = this.editorial.getBoundingClientRect();
        const headerBox = this.header?.getBoundingClientRect();
        const sticky = headerBox ? Math.max(0, Math.min(headerBox.bottom, headerBox.height)) : 0;
        const viewport = (window.visualViewport?.height || window.innerHeight) - sticky;
        const start = box.top + y - sticky;
        const distance = box.height > viewport ? Math.max(box.height - viewport, 1) : viewport;
        this.threshold = start + distance * 0.25;
        this.geometryDirty = false;
      }
      if (!this.reached && down && this.readIntent && y >= this.threshold && !blocked && !this.dialog.open) this.reached = true;
      const show = this.reached && !blocked && !this.dialog.open;
      this.tab.hidden = !show;
      if (show && !this.presented) {
        this.presented = true;
        this.tab.classList.add('is-revealing');
        this.nudgeTimer = setTimeout(() => {
          if (!this.motion.matches && !this.tab.hidden) this.tab.classList.add('is-nudging');
          this.nudgeTimer = 0;
        }, 800);
      }
      if (!show && this.presented) this.cancelNudge();
    }

    cancelNudge() {
      clearTimeout(this.nudgeTimer);
      this.nudgeTimer = 0;
      this.tab?.classList.remove('is-nudging', 'is-revealing');
    }

    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const box = this.dialog.getBoundingClientRect();
      return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
    }

    open() {
      if (this.desktop.matches || this.blocked() || this.dialog.open || this.tab.hidden) return;
      this.cancelNudge();
      this.returnFocus = true;
      this.scrollPosition = { x: window.scrollX, y: window.scrollY };
      const body = document.body;
      const scrollbar = window.innerWidth - document.documentElement.clientWidth;
      const styles = { position: 'fixed', top: `-${this.scrollPosition.y}px`, left: `-${this.scrollPosition.x}px`, width: '100%', overflow: 'hidden' };
      if (scrollbar > 0) styles['padding-right'] = `${parseFloat(getComputedStyle(body).paddingRight) + scrollbar}px`;
      this.bodyStyles = Object.entries(styles).map(([property, value]) => ({ property, value, before: body.style.getPropertyValue(property), priority: body.style.getPropertyPriority(property) }));
      this.dialog.showModal();
      this.bodyStyles.forEach(({ property, value }) => body.style.setProperty(property, value));
      this.tab.hidden = true;
      this.tab.setAttribute('aria-expanded', 'true');
      this.heading.focus({ preventScroll: true });
      // Commit the offscreen starting position before the transition to the panel.
      this.dialog.getBoundingClientRect();
      this.dialog.classList.add('is-open');
      this.loadImages();
    }

    loadImages() {
      const activate = (img) => {
        img.addEventListener('error', () => img.classList.add('is-error'), { once: true });
        img.srcset = img.dataset.drawerSrcset;
        img.src = img.dataset.drawerSrc;
        delete img.dataset.drawerSrc;
        delete img.dataset.drawerSrcset;
      };
      const images = this.querySelectorAll('img[data-drawer-src]');
      if (!('IntersectionObserver' in window)) { images.forEach(activate); return; }
      this.imageObserver?.disconnect();
      this.imageObserver = new IntersectionObserver((entries) => {
        if (!this.dialog.open || !this.dialog.classList.contains('is-open')) return;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          activate(entry.target);
          this.imageObserver.unobserve(entry.target);
        });
      }, { root: this.scroller, rootMargin: '160px 0px' });
      images.forEach((img) => this.imageObserver.observe(img));
    }

    close(immediate = false, returnFocus = true) {
      if (!this.dialog?.open) return;
      this.returnFocus = returnFocus;
      this.dialog.classList.remove('is-open');
      this.imageObserver?.disconnect();
      clearTimeout(this.closeTimer);
      if (immediate || this.motion.matches) { this.dialog.close(); this.finishClose(); }
      else this.closeTimer = setTimeout(() => { this.dialog.close(); this.finishClose(); }, 240);
    }

    finishClose() {
      if (!this.bodyStyles) return;
      clearTimeout(this.closeTimer);
      this.dialog.classList.remove('is-open');
      this.imageObserver?.disconnect();
      this.bodyStyles.forEach(({ property, value, before, priority }) => {
        // Restore only values owned by this dialog; preserve intervening owners.
        if (document.body.style.getPropertyValue(property) === value) {
          if (before) document.body.style.setProperty(property, before, priority);
          else document.body.style.removeProperty(property);
        }
      });
      this.bodyStyles = null;
      window.scrollTo({ ...{ left: this.scrollPosition.x, top: this.scrollPosition.y }, behavior: 'instant' });
      this.lastY = this.scrollPosition.y;
      this.tab.setAttribute('aria-expanded', 'false');
      this.tab.hidden = !this.reached || this.desktop.matches || this.blocked();
      if (this.returnFocus && this.isConnected) {
        if (!this.tab.hidden) this.tab.focus({ preventScroll: true });
        else if (!this.blocked()) {
          const heading = this.article.querySelector('h1');
          const before = heading?.getAttribute('tabindex');
          heading?.setAttribute('tabindex', '-1');
          heading?.focus({ preventScroll: true });
          if (before === null) heading?.removeAttribute('tabindex');
          else if (before !== undefined) heading?.setAttribute('tabindex', before);
        }
      }
      this.invalidate();
    }

    disconnectedCallback() {
      this.close(true, false);
      this.abort?.abort();
      this.abort = null;
      this.resizeObserver?.disconnect();
      this.modalObserver?.disconnect();
      this.imageObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.cancelNudge();
      clearTimeout(this.closeTimer);
    }
  }
  customElements.define('article-product-drawer', ArticleProductDrawer);
})();
